import type {
  InputHTMLAttributes,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import styles from './FormField.module.css';

interface FieldShellProps {
  id: string;
  label: string;
  required?: boolean;
  error?: string;
  hint?: string;
  compact?: boolean;
  children: React.ReactNode;
}

export function FieldShell({
  id,
  label,
  required,
  error,
  hint,
  compact,
  children,
}: FieldShellProps) {
  return (
    <div
      className={[styles.field, compact ? styles.fieldCompact : '']
        .filter(Boolean)
        .join(' ')}
    >
      <label htmlFor={id} className={styles.label}>
        {label}
        {required ? (
          <span className={styles.required} aria-hidden="true">
            *
          </span>
        ) : null}
      </label>
      {children}
      {error ? (
        <p className={styles.error} role="alert" id={`${id}-error`}>
          {error}
        </p>
      ) : hint ? (
        <p className={styles.hint} id={`${id}-hint`}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}

type TextFieldProps = InputHTMLAttributes<HTMLInputElement> & {
  id: string;
  label: string;
  error?: string;
  hint?: string;
  compact?: boolean;
};

export function TextField({
  id,
  label,
  error,
  hint,
  required,
  compact,
  className,
  ...rest
}: TextFieldProps) {
  return (
    <FieldShell
      id={id}
      label={label}
      required={required}
      error={error}
      hint={hint}
      compact={compact}
    >
      <input
        id={id}
        className={[
          styles.control,
          error ? styles.controlError : '',
          className ?? '',
        ]
          .filter(Boolean)
          .join(' ')}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={
          error ? `${id}-error` : hint ? `${id}-hint` : undefined
        }
        {...rest}
      />
    </FieldShell>
  );
}

type TextAreaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  id: string;
  label: string;
  error?: string;
  hint?: string;
  compact?: boolean;
};

export function TextArea({
  id,
  label,
  error,
  hint,
  required,
  compact,
  className,
  ...rest
}: TextAreaProps) {
  return (
    <FieldShell
      id={id}
      label={label}
      required={required}
      error={error}
      hint={hint}
      compact={compact}
    >
      <textarea
        id={id}
        className={[
          styles.control,
          styles.textarea,
          error ? styles.controlError : '',
          className ?? '',
        ]
          .filter(Boolean)
          .join(' ')}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={
          error ? `${id}-error` : hint ? `${id}-hint` : undefined
        }
        {...rest}
      />
    </FieldShell>
  );
}

type SelectFieldProps = SelectHTMLAttributes<HTMLSelectElement> & {
  id: string;
  label: string;
  error?: string;
  hint?: string;
  options: { value: string; label: string }[];
};

export function SelectField({
  id,
  label,
  error,
  hint,
  required,
  options,
  className,
  ...rest
}: SelectFieldProps) {
  return (
    <FieldShell
      id={id}
      label={label}
      required={required}
      error={error}
      hint={hint}
    >
      <select
        id={id}
        className={[
          styles.control,
          styles.select,
          error ? styles.controlError : '',
          className ?? '',
        ]
          .filter(Boolean)
          .join(' ')}
        required={required}
        aria-invalid={error ? true : undefined}
        {...rest}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </FieldShell>
  );
}
